import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, User, FileText, Brain, Zap, Save, Trash2 } from 'lucide-react';
import {
  AdminClientApi,
  ClientUserInterface,
  ClientBelief,
  ClientThoughtWithAuthor,
  BeliefInput,
} from '../../Admin-ClientLibrary';
import { getApiUrl } from '../../config/api';
import { useRecentEntities } from '../../contexts/RecentEntitiesContext';
import './AISupportingBeliefDetail.css';

// Initialize the Admin Client Library
const getToken = (): string => {
  return localStorage.getItem('authToken') || '';
};

const adminApi = new AdminClientApi(getApiUrl(), getToken);

const AISupportingBeliefDetail: React.FC = () => {
  const { beliefId } = useParams<{ beliefId: string }>();
  const navigate = useNavigate();
  const { addRecentEntity } = useRecentEntities();

  // Data state
  const [belief, setBelief] = useState<ClientBelief | null>(null);
  const [user, setUser] = useState<ClientUserInterface | null>(null);
  const [thought, setThought] = useState<ClientThoughtWithAuthor | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [beliefSystemText, setBeliefSystemText] = useState<string>('');
  const [reasoning, setReasoning] = useState<'CRAZY' | 'VERY_INTELLIGENT' | 'NORMAL'>('NORMAL');

  // API operation state
  const [generatingBeliefSystem, setGeneratingBeliefSystem] = useState<boolean>(false);
  const [gettingSupportingBeliefs, setGettingSupportingBeliefs] = useState<boolean>(false);
  const [savingBeliefs, setSavingBeliefs] = useState<{ [key: number]: boolean }>({});

  // Results state
  const [generatedBeliefSystem, setGeneratedBeliefSystem] = useState<{
    belief_system: string;
    reasoning: 'CRAZY' | 'VERY_INTELLIGENT' | 'NORMAL';
  } | null>(null);
  const [supportingBeliefsResults, setSupportingBeliefsResults] = useState<{
    existing: BeliefInput[];
    new: BeliefInput[];
  } | null>(null);

  // Editable new beliefs state
  const [editableNewBeliefs, setEditableNewBeliefs] = useState<BeliefInput[]>([]);

  useEffect(() => {
    if (beliefId) {
      const beliefIdNum = parseInt(beliefId, 10);

      if (isNaN(beliefIdNum) || beliefIdNum <= 0) {
        setError('Invalid belief ID in URL');
        setLoading(false);
        return;
      }

      fetchBeliefData(beliefIdNum);
    }
  }, [beliefId]);

  const fetchBeliefData = async (beliefIdNum: number): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // Fetch belief data using getBelief API
      const beliefResponse = await adminApi.getBelief({ beliefId: beliefIdNum });

      if (beliefResponse.success && beliefResponse.belief) {
        setBelief(beliefResponse.belief);
        addRecentEntity('belief', beliefResponse.belief);

        // Fetch user data
        try {
          const userResponse = await adminApi.getUser({ userId: beliefResponse.belief.user_id });
          if (userResponse.success && userResponse.user) {
            setUser(userResponse.user);
            addRecentEntity('user', userResponse.user);
          } else {
            console.warn('User not found for user_id:', beliefResponse.belief.user_id);
          }
        } catch (userError) {
          console.error('Error fetching user:', userError);
        }

        // If belief has a thought_id, fetch the related thought
        if (beliefResponse.belief.thought_id) {
          try {
            const thoughtResponse = await adminApi.getThought({
              thoughtId: beliefResponse.belief.thought_id,
            });

            if (thoughtResponse.success && thoughtResponse.thought) {
              setThought(thoughtResponse.thought);
              addRecentEntity('thought', thoughtResponse.thought);
            } else {
              console.warn('Thought not found for thought_id:', beliefResponse.belief.thought_id);
            }
          } catch (thoughtError) {
            console.error('Error fetching thought:', thoughtError);
          }
        }
      } else {
        setError(beliefResponse.message || 'Belief not found');
        return;
      }
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBeliefSystem = async (): Promise<void> => {
    if (!belief) return;

    try {
      setGeneratingBeliefSystem(true);
      setError(null);

      const response = await adminApi.generateRobustBeliefSystem({
        beliefId: belief.id,
      });

      if (response.success && response.robust_belief_system) {
        setGeneratedBeliefSystem(response.robust_belief_system);
        setBeliefSystemText(response.robust_belief_system.belief_system);
        setReasoning(response.robust_belief_system.reasoning);
      } else {
        setError(response.message || 'Failed to generate belief system');
      }
    } catch (err: any) {
      console.error('Error generating belief system:', err);
      setError(err.message || 'Failed to generate belief system');
    } finally {
      setGeneratingBeliefSystem(false);
    }
  };

  const handleGetSupportingBeliefs = async (): Promise<void> => {
    if (!belief || !beliefSystemText.trim()) {
      setError('Please enter a belief system first');
      return;
    }

    try {
      setGettingSupportingBeliefs(true);
      setError(null);

      const response = await adminApi.getSupportingBeliefsFromBeliefSystem({
        beliefId: belief.id,
        beliefSystem: {
          belief_system: beliefSystemText,
          reasoning: reasoning,
        },
      });

      if (response.success && response.result) {
        setSupportingBeliefsResults(response.result);
        setEditableNewBeliefs([...response.result.new]);
      } else {
        setError(response.message || 'Failed to get supporting beliefs');
      }
    } catch (err: any) {
      console.error('Error getting supporting beliefs:', err);
      setError(err.message || 'Failed to get supporting beliefs');
    } finally {
      setGettingSupportingBeliefs(false);
    }
  };

  const getPositionColor = (position: string): string => {
    const pos = position.toLowerCase();
    switch (pos) {
      case 'believe':
        return 'believe';
      case 'disbelieve':
        return 'disbelieve';
      case 'unsure':
        return 'unsure';
      default:
        return 'unsure';
    }
  };

  const getRoleColor = (type: string): string => {
    switch (type) {
      case 'premium':
        return 'premium';
      case 'beta':
        return 'beta';
      case 'free':
        return 'free';
      default:
        return 'free';
    }
  };

  const updateEditableBeliefText = (index: number, newText: string): void => {
    setEditableNewBeliefs((prev) =>
      prev.map((belief, i) => (i === index ? { ...belief, text: newText } : belief))
    );
  };

  const updateEditableBeliefPosition = (
    index: number,
    newPosition: 'BELIEVE' | 'DISBELIEVE' | 'UNSURE'
  ): void => {
    setEditableNewBeliefs((prev) =>
      prev.map((belief, i) => (i === index ? { ...belief, position: newPosition } : belief))
    );
  };

  const cycleBeliefPosition = (index: number): void => {
    const currentBelief = editableNewBeliefs[index];
    let nextPosition: 'BELIEVE' | 'DISBELIEVE' | 'UNSURE';

    switch (currentBelief.position) {
      case 'BELIEVE':
        nextPosition = 'DISBELIEVE';
        break;
      case 'DISBELIEVE':
        nextPosition = 'UNSURE';
        break;
      case 'UNSURE':
        nextPosition = 'BELIEVE';
        break;
      default:
        nextPosition = 'BELIEVE';
    }

    updateEditableBeliefPosition(index, nextPosition);
  };

  const removeBeliefFromArray = (index: number): void => {
    setEditableNewBeliefs((prev) => prev.filter((_, i) => i !== index));
  };

  const saveBeliefChanges = async (index: number): Promise<void> => {
    if (!belief || !user) return;

    const beliefToSave = editableNewBeliefs[index];
    if (!beliefToSave.text.trim()) {
      setError('Belief text cannot be empty');
      return;
    }

    try {
      setSavingBeliefs((prev) => ({ ...prev, [index]: true }));
      setError(null);

      const response = await adminApi.saveNewSupportingThoughtAndBelief({
        userId: user.account_id,
        beliefId: belief.id,
        newThoughtText: beliefToSave.text,
        supportingBeliefPosition: beliefToSave.position,
      });

      if (response.success) {
        // Remove the saved belief from the editable list since it's now saved
        removeBeliefFromArray(index);
        // TODO: Optionally refresh the existing beliefs list to show the new one
        console.log('Belief saved successfully:', response);
      } else {
        setError(response.message || 'Failed to save belief');
      }
    } catch (err: any) {
      console.error('Error saving belief:', err);
      setError(err.message || 'Failed to save belief');
    } finally {
      setSavingBeliefs((prev) => ({ ...prev, [index]: false }));
    }
  };

  if (loading) {
    return (
      <div className="ai-supporting-belief-page">
        <div className="loading">Loading belief details...</div>
      </div>
    );
  }

  if (error || !belief) {
    return (
      <div className="ai-supporting-belief-page">
        <div className="error-message">
          <p>Error: {error || 'Failed to load data'}</p>
          <button onClick={() => navigate('/ai-supporting-beliefs')} className="btn-secondary">
            Back to AI Supporting Beliefs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-supporting-belief-page">
      <div className="ai-supporting-belief-header">
        <button onClick={() => navigate('/ai-supporting-beliefs')} className="back-button">
          <ArrowLeft size={16} />
          Back to AI Supporting Beliefs
        </button>

        <div className="ai-supporting-belief-title">
          <div className="belief-icon">
            <Brain size={24} />
          </div>
          <div className="belief-title-info">
            <h1>AI Supporting Beliefs</h1>
            <div className="belief-meta">
              <span className={`belief-position ${getPositionColor(belief.position)}`}>
                {belief.position.toUpperCase()}
              </span>
              <span className="belief-id">Belief ID: {belief.id}</span>
              <span className="belief-created">
                Created: {new Date(belief.createdAt).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Belief Information Cards - Same as BeliefSupport */}
      <div className="belief-info-section">
        <div className="info-cards-grid">
          {/* User Info Card */}
          <div className="info-card">
            <div className="info-card-header">
              <User size={20} />
              <h3>User Information</h3>
            </div>
            <div className="info-card-content">
              {user && (
                <div className="user-summary">
                  <div className="user-avatar">{user.name.charAt(0).toUpperCase()}</div>
                  <div className="user-info">
                    <h4>{user.name}</h4>
                    <p>{user.email}</p>
                    <span className={`role-badge ${getRoleColor(user.type)}`}>{user.type}</span>
                  </div>
                </div>
              )}
              {!user && (
                <div className="user-summary">
                  <div className="user-avatar">U</div>
                  <div className="user-info">
                    <h4>User #{belief.user_id}</h4>
                    <p>Loading user details...</p>
                  </div>
                </div>
              )}
              {user && (
                <div className="belief-meta-info">
                  <p>
                    <button
                      className="clickable-id-button"
                      onClick={() => navigate(`/users/${user.account_id}`)}
                    >
                      <strong>User ID:</strong> {user.account_id}
                    </button>
                  </p>
                  <p>
                    <strong>Account Created:</strong>{' '}
                    {new Date(user.createdAt).toLocaleDateString()}
                  </p>
                  {user.country && (
                    <p>
                      <strong>Country:</strong> {user.country}
                    </p>
                  )}
                  {user.birthdate && (
                    <p>
                      <strong>Age Category:</strong> {user.ageCategory}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Thought Details Card */}
          <div className="info-card">
            <div className="info-card-header">
              <FileText size={20} />
              <h3>Thought Details</h3>
            </div>
            <div className="info-card-content">
              {thought && (
                <div className="thought-details">
                  <div className="thought-content">
                    <h4>Thought Content</h4>
                    <p>{thought.text}</p>
                  </div>
                  <div className="belief-meta-info">
                    <p>
                      <button
                        className="clickable-id-button"
                        onClick={() => navigate(`/thoughts/${thought.content_id}`)}
                      >
                        <strong>Thought ID:</strong> {thought.content_id}
                      </button>
                    </p>
                    <p>
                      <strong>Created:</strong> {new Date(thought.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              )}
              {!thought && belief.thought_id && (
                <div className="thought-details">
                  <div className="thought-content">
                    <h4>Thought Content</h4>
                    <p className="thought-loading">Loading thought details...</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* AI Tools Section */}
      <div className="ai-tools-section">
        <div className="section-header">
          <h3>AI Belief System Tools</h3>
          <p>Generate and analyze belief systems using AI</p>
        </div>

        {/* Belief System Input */}
        <div className="belief-system-card">
          <div className="belief-system-header">
            <h4>Belief System</h4>
            <p>Enter or generate a comprehensive belief system</p>
          </div>
          <div className="belief-system-content">
            <div className="form-group">
              <label htmlFor="beliefSystem">Belief System Text</label>
              <textarea
                id="beliefSystem"
                value={beliefSystemText}
                onChange={(e) => setBeliefSystemText(e.target.value)}
                placeholder="Enter a detailed belief system that explains the reasoning behind this belief..."
                rows={6}
                className="belief-system-textarea"
              />
            </div>

            <div className="form-group">
              <label htmlFor="reasoning">Reasoning Type</label>
              <select
                id="reasoning"
                value={reasoning}
                onChange={(e) =>
                  setReasoning(e.target.value as 'CRAZY' | 'VERY_INTELLIGENT' | 'NORMAL')
                }
                className="reasoning-select"
              >
                <option value="NORMAL">Normal</option>
                <option value="VERY_INTELLIGENT">Very Intelligent</option>
                <option value="CRAZY">Crazy</option>
              </select>
            </div>

            <div className="button-group">
              <button
                onClick={handleGenerateBeliefSystem}
                disabled={generatingBeliefSystem}
                className="btn-primary"
              >
                <Brain size={16} />
                {generatingBeliefSystem ? 'Generating...' : 'Generate Belief System'}
              </button>

              <button
                onClick={handleGetSupportingBeliefs}
                disabled={gettingSupportingBeliefs || !beliefSystemText.trim()}
                className="btn-secondary"
              >
                <Zap size={16} />
                {gettingSupportingBeliefs
                  ? 'Getting...'
                  : 'Get Supporting Beliefs From Belief System'}
              </button>
            </div>
          </div>
        </div>

        {/* Results Section */}
        {supportingBeliefsResults && (
          <div className="results-section">
            <h4>Supporting Beliefs Results</h4>

            <div className="results-grid">
              {/* Existing Beliefs */}
              <div className="result-card">
                <div className="result-card-header">
                  <h5>Existing Supporting Beliefs ({supportingBeliefsResults.existing.length})</h5>
                  <p>Beliefs already in the system that support this belief</p>
                </div>
                <div className="result-card-content">
                  {supportingBeliefsResults.existing.length > 0 ? (
                    <ul className="belief-list">
                      {supportingBeliefsResults.existing.map((belief, index) => (
                        <li key={index} className="belief-item">
                          <div className="belief-text">{belief.text}</div>
                          <div
                            className={`belief-position-tag position-${belief.position.toLowerCase()}`}
                          >
                            {belief.position}
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="no-results">No existing supporting beliefs found</p>
                  )}
                </div>
              </div>

              {/* New Beliefs */}
              <div className="result-card">
                <div className="result-card-header">
                  <h5>New Supporting Beliefs ({editableNewBeliefs.length})</h5>
                  <p>AI-generated beliefs that could support this belief (editable)</p>
                </div>
                <div className="result-card-content">
                  {editableNewBeliefs.length > 0 ? (
                    <div className="editable-belief-list">
                      {editableNewBeliefs.map((belief, index) => (
                        <div key={index} className="editable-belief-item">
                          <div className="belief-text-editor">
                            <label htmlFor={`belief-text-${index}`}>Belief Text</label>
                            <textarea
                              id={`belief-text-${index}`}
                              value={belief.text}
                              onChange={(e) => updateEditableBeliefText(index, e.target.value)}
                              className="belief-text-textarea"
                              rows={3}
                            />
                          </div>
                          <div className="belief-actions-row">
                            <div className="belief-position-display">
                              <div
                                className={`belief-position-pill clickable ${getPositionColor(
                                  belief.position
                                )}`}
                                onClick={() => cycleBeliefPosition(index)}
                                title="Click to cycle through positions"
                              >
                                {belief.position}
                              </div>
                            </div>
                            <div className="belief-action-buttons">
                              <button
                                className="action-btn save-btn"
                                onClick={() => saveBeliefChanges(index)}
                                disabled={savingBeliefs[index]}
                                title={savingBeliefs[index] ? 'Saving...' : 'Save changes'}
                              >
                                {savingBeliefs[index] ? (
                                  <div className="loading-spinner" />
                                ) : (
                                  <Save size={16} />
                                )}
                              </button>
                              <button
                                className="action-btn trash-btn"
                                onClick={() => removeBeliefFromArray(index)}
                                title="Remove belief"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-results">No new supporting beliefs generated</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="error-banner">
            <p>{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AISupportingBeliefDetail;
